/* eslint-disable no-nested-ternary */
import UiButton from '@components/ui/button/UiButton';
import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Select, Spin, Upload } from 'antd';
import { useTranslation } from 'next-i18next';
import { FormElement, FormModel, FormValues } from '@models/form';
import StateEnum from '@constants/state.enum';
import { UploadOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import React, { useEffect, useState } from 'react';
import { postCv, postPositionCv, uploadCv } from '@services/form/form-http.service';
import dayjs from 'dayjs';
import useNotification from '@hooks/notification/useNotification';
import useRecaptcha from '@hooks/re-captcha/useRecaptcha';
import LookupGroupType, { LookUp, LookupType } from '@models/lookup';
import { getGroupLookup, getLookup } from '@services/lookup/lookup-http.service';
import TextArea from 'antd/lib/input/TextArea';
import styles from './FormBuilder.module.scss';

const { Option, OptGroup } = Select;

type FormBuilderProps = {
  formFields: FormModel;
  slug: string;
  positionId?: string | undefined;
  lang: string;
  clientColor?: string;
};

const FormBuilder: React.FC<FormBuilderProps> = ({ formFields, slug, positionId, lang, clientColor }) => {
  const { t: translate, i18n } = useTranslation('common');
  const [form] = Form.useForm();
  const [uploadList, setUploadList] = useState<boolean>(true);
  const [lookupSelectInfo, setLookupSelectInfo] = useState<LookupType>();
  const [lookupMultiselectInfo, setLookupMultiselectInfo] = useState<LookupType>();
  const [lookupGroupMultiselectInfo, setLookupGroupMultiselectInfo] = useState<LookupGroupType>();
  const [checkboxTerms, setCheckboxTerms] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<FormData>();
  const [submitted, setSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useRecaptcha(submitted);
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dateFormat = 'DD/MM/YYYY';

  const [generalError] = useNotification(StateEnum.ERROR, translate('smth_went_wrong'), translate('oops_wrong'));
  const [requiredFields] = useNotification(
    StateEnum.WARNING,
    translate('incomleted_form'),
    translate('required_values'),
  );
  const [uploadCvMessage] = useNotification(
    StateEnum.WARNING,
    'Unable to upload your CV',
    'Your CV must be smaller than 1.5MB',
  );

  useEffect(() => {
    const submitRequest = async (values: FormValues) => {
      const sendCvFields = {
        slug,
        recaptchaToken,
        lastName: values.lastName,
        firstName: values.firstName,
        email: values.email,
        residentialArea: values.residentialArea,
        address: values.address,
        addressNumber: values.addressNumber,
        postCode: values.postCode,
        dateOfBirth: values.dateOfBirth && dayjs(values.dateOfBirth).format('YYYY/MM/DD'),
        mobilePhoneNumber: values.mobilePhoneNumber?.toString().replace(/\+/g, ''),
        educationLevel:
          values.educationLevel && Array.isArray(values.educationLevel)
            ? values.educationLevel.map((item) => item.value)
            : values.educationLevel?.key,
        location:
          values.location && Array.isArray(values.location)
            ? values.location.map((item) => item.value)
            : values.location?.key,
        gender:
          values.gender && Array.isArray(values.gender) ? values.gender.map((item) => item.value) : values.gender?.key,
        drivingLicense:
          values.drivingLicense && Array.isArray(values.drivingLicense)
            ? values.drivingLicense.map((item) => item.value)
            : values.drivingLicense?.key,
        residenceAreaList:
          values.residenceAreaList && Array.isArray(values.residenceAreaList)
            ? values.residenceAreaList.map((item) => item.value)
            : values.residenceAreaList?.key,
        department:
          values.department && Array.isArray(values.department)
            ? values.department.map((item) => item.key)
            : values.department?.key,
        position:
          values.position && Array.isArray(values.position)
            ? values.position.map((item) => item.value)
            : values.position?.key,
        employmentType:
          values.employmentType && Array.isArray(values.employmentType)
            ? values.employmentType.map((item) => item.value)
            : values.employmentType?.key,
        informedBy:
          values.informedBy && Array.isArray(values.informedBy)
            ? values.informedBy.map((item) => item.value)
            : values.informedBy?.key,
        phoneNumber: values.phoneNumber?.toString().replace(/\+/g, ''),
        comments: values.comments,
      };

      const uploadFile = async (person: number) => {
        try {
          await uploadCv(slug, person.toString(), uploadedFile!);
          window.location.assign(`/${i18n.language}/${slug}/thank-you`);
        } catch (e: any) {
          generalError();
        }
      };

      if (positionId) {
        try {
          const person = await postPositionCv(positionId, sendCvFields);
          await uploadFile(person);
          setLoading(false);
        } catch (e: any) {
          setSubmitted(false);
          generalError();
          setLoading(false);
        }
      } else {
        try {
          const person = await postCv(sendCvFields);
          await uploadFile(person);
        } catch (e: any) {
          setSubmitted(false);
          generalError();
          setLoading(false);
        }
      }
    };

    if (recaptchaToken) {
      submitRequest(form.getFieldsValue());
      setRecaptchaToken('');
    }
  }, [form, positionId, recaptchaToken]);

  const onFinish = () => {
    setLoading(true);
    setSubmitted(true);
  };

  const onFinishFailed = () => {
    requiredFields();
  };

  const setupFileUploaded = (val: any) => {
    if (val.size < 1572864) {
      const data = new FormData();
      data.append('file', val.originFileObj);
      setUploadedFile(data);
    } else {
      setUploadedFile(undefined);
      form.resetFields(['cv_file']);
    }
  };

  const fetchSelectLookup = async (name: string) => {
    try {
      setSelectLoading(true);
      const lookupItems: LookupType = await getLookup(slug, name, lang);
      setLookupSelectInfo(lookupItems);
      setSelectLoading(false);
    } catch {
      setSelectLoading(true);
      generalError();
      setSelectLoading(false);
    }
  };

  const fetchMultiSelectLookup = async (name: string) => {
    try {
      setSelectLoading(true);
      const lookupItems: LookupType = await getLookup(slug, name, lang);
      setLookupMultiselectInfo(lookupItems);
      setSelectLoading(false);
    } catch {
      setSelectLoading(true);
      generalError();
      setSelectLoading(false);
    }
  };

  const fetchGroupLookup = async (name: string) => {
    try {
      setLoading(true);
      const lookupItems: LookupGroupType = await getGroupLookup(slug, name, lang);
      setLookupGroupMultiselectInfo(lookupItems);
      setLoading(false);
    } catch {
      setLoading(true);
      generalError();
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isLt2M = file.size / 1024 / 1024 < 1.5;
    if (!isLt2M) {
      uploadCvMessage('Unable to upload your CV');
      setUploadList(false);
      return isLt2M;
    }
    setUploadList(true);
    return isLt2M;
  };

  const handleFormatter = (value: any) => {
    value = value.replace(/^\+/, '').replace(/[^0-9.]/g, '');
    if (value.length >= 15) {
      value = value.substring(0, 15);
    }
    return value;
  };

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        name={`${slug}-form`}
        className={styles.generalForm}
      >
        {formFields.map((formElement: FormElement) => (
          <Form.Item
            key={formElement.id}
            name={formElement.name}
            // eslint-disable-next-line prettier/prettier
            label={formElement.description !== 'checkbox' && formElement.description !== 'upload' && formElement.label}
            rules={[
              formElement.description === 'email'
                ? { required: formElement.required, message: translate('email_warning'), type: 'email' }
                : formElement.description === 'upload'
                ? {
                    required: formElement.required,
                    message: translate('cv_error'),
                  }
                : {
                    required: formElement.required,
                    message: translate('required_fields', { name: formElement.label }),
                  },
            ]}
          >
            <Form.Item name={formElement.name} className={styles.formItemInside}>
              {(formElement.description === 'text' || formElement.description === 'email') && (
                <Input maxLength={formElement.maxLength} placeholder={formElement.placeholder} />
              )}
              {formElement.description === 'number' && (
                <InputNumber formatter={handleFormatter} className={styles.fullWidthField} controls={false} />
              )}
              {formElement.description === 'date' && formElement.name === 'dateOfBirth' && (
                <DatePicker
                  format={dateFormat}
                  className={styles.fullWidthField}
                  disabledDate={(current) => current.isAfter(dayjs().subtract(17, 'year'))}
                  placeholder="Επιλέξτε από το ημερολόγιο"
                  defaultPickerValue={dayjs().subtract(17, 'year')}
                  inputReadOnly
                  onChange={async (value) => {
                    await form.setFieldsValue({ [`${formElement.name}`]: value });
                  }}
                />
              )}
              {formElement.description === 'date' && formElement.name !== 'dateOfBirth' && (
                <DatePicker
                  format={dateFormat}
                  className={styles.fullWidthField}
                  inputReadOnly
                  onChange={async (value) => {
                    await form.setFieldsValue({ [`${formElement.name}`]: value });
                  }}
                />
              )}
              {formElement.description === 'select' && (
                <Select
                  placeholder="Please select"
                  optionFilterProp="children"
                  onChange={async (value, option: any) => {
                    await form.setFieldsValue({
                      [`${formElement.name}`]: { key: option.value, label: option.children },
                    });
                  }}
                  onClick={() => fetchSelectLookup(formElement.name)}
                  data-testid="custom-select-field"
                >
                  {selectLoading ? (
                    <Option data-testid="spinner-option">
                      <Spin size="default" />
                    </Option>
                  ) : lookupSelectInfo ? (
                    lookupSelectInfo.map((optionValue) => {
                      return (
                        <Option key={optionValue.id} value={optionValue.id}>
                          {optionValue.name}
                        </Option>
                      );
                    })
                  ) : (
                    ''
                  )}
                </Select>
              )}
              {formElement.description === 'multi-select' && (
                <Select
                  mode="multiple"
                  allowClear
                  onChange={async (title, option: any) => {
                    await form.setFieldsValue({
                      [`${formElement.name}`]: option,
                    });
                  }}
                  className={styles.fullWidthField}
                  placeholder="Please select"
                  onClick={() => fetchMultiSelectLookup(formElement.name)}
                  onClear={async () => {
                    form.resetFields([`${formElement.name}`]);
                    await form.validateFields([`${formElement.name}`]);
                  }}
                  data-testid="custom-multiselect-field"
                >
                  {selectLoading ? (
                    <Option data-testid="spinner-option">
                      <Spin size="default" />
                    </Option>
                  ) : lookupMultiselectInfo ? (
                    lookupMultiselectInfo.map((optionValue) => {
                      return (
                        <Option key={optionValue.id} value={optionValue.name}>
                          {optionValue.name}
                        </Option>
                      );
                    })
                  ) : (
                    ''
                  )}
                </Select>
              )}
              {formElement.description === 'group-multi-select' && (
                <Select
                  mode="multiple"
                  allowClear
                  onChange={async (title, option: any) => {
                    await form.setFieldsValue({
                      [`${formElement.name}`]: option,
                    });
                  }}
                  className={styles.fullWidthField}
                  placeholder="Please select"
                  onClear={async () => {
                    form.resetFields([`${formElement.name}`]);
                    await form.validateFields([`${formElement.name}`]);
                  }}
                  onClick={() => fetchGroupLookup(formElement.name)}
                >
                  {selectLoading ? (
                    <Option>
                      <Spin size="default" />
                    </Option>
                  ) : lookupGroupMultiselectInfo ? (
                    lookupGroupMultiselectInfo.map((optValue) => {
                      return (
                        <OptGroup key={optValue.id} label={optValue.name}>
                          {optValue.children.map((optionValue: LookUp) => {
                            return (
                              <Option key={optionValue.id} value={optionValue.id}>
                                {optionValue.name}
                              </Option>
                            );
                          })}
                        </OptGroup>
                      );
                    })
                  ) : (
                    ''
                  )}
                </Select>
              )}
              {formElement.description === 'textarea' && (
                <TextArea rows={4} placeholder={formElement.placeholder} maxLength={formElement.maxLength} />
              )}
              {formElement.description === 'upload' && (
                <Upload
                  onChange={(value) => setupFileUploaded(value.file)}
                  accept=".doc,.docx,.pdf"
                  maxCount={1}
                  beforeUpload={beforeUpload}
                  showUploadList={uploadList}
                  onRemove={async () => {
                    form.resetFields([`${formElement.name}`]);
                    await form.validateFields([`${formElement.name}`]);
                  }}
                >
                  <Button icon={<UploadOutlined />}>{formElement.label}</Button>
                  <div className={styles.wrapUploadText}>
                    <div className={`h6 ${styles.uploadText}`}>{translate('file_type')}</div>
                    <div className={`h6 ${styles.uploadText}`}>{translate('maximum_file_size')}</div>
                  </div>
                </Upload>
              )}
            </Form.Item>
          </Form.Item>
        ))}
        <Form.Item name="terms" rules={[{ required: true }]}>
          <Checkbox
            checked={checkboxTerms}
            onChange={() => {
              setCheckboxTerms(!checkboxTerms);
              form.setFieldsValue({ [`terms`]: checkboxTerms });
            }}
          />
          <span className={styles.termsText}>
            {translate('i_accept')}
            <a
              style={{ color: clientColor || '#ff6b8c' }}
              href={`/${i18n?.language}/${slug}/terms`}
              target="_blank"
              rel="noreferrer"
            >
              {translate('terms_of_use')}
            </a>
          </span>
          {!checkboxTerms && (
            <div className={styles.termsNotification}>
              {translate('please_accept')} {translate('terms_of_use')}
            </div>
          )}
        </Form.Item>
        <Form.Item>
          <UiButton type="primary" size="small" htmlType="submit" disabled={loading || !checkboxTerms}>
            {loading ? <Spin size="default" /> : translate('submit')}
          </UiButton>
        </Form.Item>
      </Form>
    </>
  );
};

FormBuilder.defaultProps = {
  positionId: '',
  clientColor: '',
};

export default FormBuilder;
